import React, { useEffect } from 'react';

import PropTypes from 'prop-types';

import { useListenForGlobal } from '@ulta/core/hooks/useListenForGlobal/useListenForGlobal';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { useShoppingAgentContext } from '@ulta/core/providers/ShoppingAgentProvider/ShoppingAgentProvider';
import constants from '@ulta/core/utils/constants/constants';
import datacapture, { patchDataLayer } from '@ulta/core/utils/datacapture/datacapture';
import { createLogger, LOG_TOPIC } from '@ulta/core/utils/devMode/devMode';
import { handleEmptyObjects, isEmptyObject } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { formatTokenForGoogle, getShoppingAgentToken } from '@ulta/core/utils/shoppingAgentSession/shoppingAgentSession';
import { getStorage, removeStorage, setStorage } from '@ulta/core/utils/storage/storage';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys';

import * as shoppingAgentUtil from '@ulta/utils/shoppingAgent/shoppingAgent';

import * as utils from './ShoppingAgent';

let _shoppingAgentLogger;
const getShoppingAgentLogger = () => {
  if( !_shoppingAgentLogger ){
    _shoppingAgentLogger = createLogger( {
      topic: LOG_TOPIC.Sdk,
      scope: '[SDK] ShoppingAgent >'
    } );
  }
  return _shoppingAgentLogger;
};

export const GOOGLE_SHOPPING_AGENT_EVENT = {
  GoogleAgentLoaded: 'google-agent-loaded',
  GoogleAgentOpenChanged: 'google-agent-open-changed',
  GoogleAgentAddToCart: 'google-agent-add-to-cart',
  GoogleAgentQuerySubmitted: 'google-agent-query-sent',
  GoogleAgentSingleItemClicked: 'google-agent-single-item-clicked'
};

export const SHOW_DETAILS = 'show_details';
export const PENDING_SHOPPING_AGENT_EVENT = 'pendingShoppingAgentClickEvent';

/**
 * Initializes the Google Shopping Agent SDK. This component is a pure side-effect
 * component and does not render any UI. It waits for the main ULTA SDK to be
 * available, then injects the Google Shopping Agent script.
 *
 * @param {object} props - The component props.
 * @param {object} [props.baseScript] - The base script configuration. If not provided, the component does nothing.
 * @param {string} [props.baseScript.src] - The URL for the Google Shopping Agent script.
 * @param {string} [props.baseScript.tagId] - The ID for the script tag.
 * @param {string} [props.sdkCustomerId] - The customer ID provided by the SDK. If not provided, the component does nothing.
 * @param {boolean} [props.enableCartQuantityIncrementFeatureFlag] - Flag to control the cart quantity increment feature.
 * @returns {null} This component does not render anything.
 */
const ShoppingAgent = ( { baseScript, sdkCustomerId, enableCartQuantityIncrementFeatureFlag, agentSettings } ) => {
  const { src, tagId } = baseScript || {};
  const { isNative } = useAppConfigContext();
  const { isShoppingAgentSiteWideToggleEnabled } = useShoppingAgentContext();
  const { openOverlay } = useOverlay();

  useEffect( () => {
    try {
      const pendingEvent = getStorage( { key: PENDING_SHOPPING_AGENT_EVENT, secure: false } );
      if( pendingEvent && !isEmptyObject( pendingEvent ) ){
        datacapture.processDataCaptureEvent( pendingEvent );
        removeStorage( { key: PENDING_SHOPPING_AGENT_EVENT, secure: false } );
      }
    }
    catch ( err ){
      getShoppingAgentLogger().error( 'Failed to process pendingShoppingAgentClickEvent', err );
    }

    window.addEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentOpenChanged, utils.handleGoogleAgentOpen );
    window.addEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentLoaded, utils.handleGoogleAgentLoaded );
    window.addEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentAddToCart, utils.handleGoogleAgentAddToCart );
    window.addEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentQuerySubmitted, utils.handleGoogleAgentQuerySubmitted );
    window.addEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentSingleItemClicked, utils.handleGoogleAgentSingleItemClicked );

    return () => {
      window.removeEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentOpenChanged, utils.handleGoogleAgentOpen );
      window.removeEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentLoaded, utils.handleGoogleAgentLoaded );
      window.removeEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentAddToCart, utils.handleGoogleAgentAddToCart );
      window.removeEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentQuerySubmitted, utils.handleGoogleAgentQuerySubmitted );
      window.removeEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentSingleItemClicked, utils.handleGoogleAgentSingleItemClicked );
    };
  }, [] );

  useListenForGlobal( {
    watch: () => !!sdkCustomerId && !!src && !!global.ULTA_SDK.shoppingAgentModel,
    callback: () => utils.initAgent(
      { src, tagId, sdkCustomerId, isShoppingAgentSiteWideToggleEnabled, isNative, enableCartQuantityIncrementFeatureFlag, agentSettings },
      { openOverlay }
    ),
    delay: 300
  } );

  return <></>;
};

ShoppingAgent.propTypes = {
  /** The base script configuration. */
  baseScript: PropTypes.shape( {
    /** The URL for the Google Shopping Agent script. */
    src: PropTypes.string,
    /** The ID for the script tag. */
    tagId: PropTypes.string
  } ),
  /** The customer ID provided by the SDK. */
  sdkCustomerId: PropTypes.string,
  /** Wheather to hide the entry chat toogle */
  isShoppingAgentSiteWideToggleEnabled: PropTypes.bool,
  /** Flag to enable/disable cart quantity increment */
  enableCartQuantityIncrementFeatureFlag: PropTypes.bool
};

export default ShoppingAgent;

/**
 * Initializes the Google Shopping Agent. This involves setting up the global
 * `googleAgentSettings` object and dynamically injecting the agent script
 * into the document head.
 *
 * @param {object} data - The configuration data.
 * @param {string} data.sdkCustomerId - The customer ID for the agent.
 * @param {string} data.src - The source URL for the agent script.
 * @param {string} [data.tagId] - Optional ID for the script tag.
 * @param {boolean} [data.isShoppingAgentSiteWideToggleEnabled] - Whether the toggle is enabled site-wide.
 * @param {boolean} [data.isNative] - Whether the application is running in a native webview.
 * @param {object} methods - The functional dependencies.
 * @param {Function} methods.openOverlay - Function to open an overlay.
 * @returns {Promise<void>}
 */
export const initAgent = async( data, methods ) => {
  const { sdkCustomerId, src, tagId, isShoppingAgentSiteWideToggleEnabled, isNative, enableCartQuantityIncrementFeatureFlag, agentSettings } = handleEmptyObjects( data );
  const { openOverlay } = handleEmptyObjects( methods );
  const isReEntry = !!window.shoppingAgentScriptLoaded;

  getShoppingAgentLogger().log( 'initAgent', {
    isReEntry,
    sdkCustomerId,
    existingSettingsId: window.googleAgentSettings?.id
  } );

  const handleCartUpdate = utils.composeHandleCartUpdate( {}, { openOverlay } );

  window.googleAgentSettings = {
    id: sdkCustomerId,
    cartVersion: 2,
    tokenFunc: utils.handleTokenFunc,
    handleGetCart: utils.handleGetCart,
    handleCartUpdate,
    settings: {
      ...agentSettings,
      'cart-quantity-increment': true
    }
  };

  if( enableCartQuantityIncrementFeatureFlag ){
    window.googleAgentSettings.settings['cart-quantity-increment-feature-flag'] = true;
  }

  if( isNative || isShoppingAgentSiteWideToggleEnabled === false ){
    window.googleAgentSettings.settings['hide-entry-toggle'] = true;
  }

  if( isReEntry ){
    getShoppingAgentLogger().warn( 'initAgent re-entry', {
      note: 'Google SDK may have captured old tokenFunc reference at load time',
      googleAgentAvailable: typeof window.googleAgent,
      googleAgentMethods: window.googleAgent ? Object.keys( window.googleAgent ) : 'N/A'
    } );
    return;
  }

  const s = document.createElement( 'script' );
  s.src = src;
  if( tagId ){
    s.id = tagId;
  }
  s.async = true;

  s.onload = function(){
    window.shoppingAgentScriptLoaded = true;
    getShoppingAgentLogger().log( 'script loaded', {
      googleAgentAvailable: typeof window.googleAgent,
      googleAgentMethods: window.googleAgent ? Object.keys( window.googleAgent ) : 'not yet'
    } );
  };
  s.onerror = () => {
    window.shoppingAgentScriptLoaded = false;
    getShoppingAgentLogger().error( 'script failed to load' );
  };
  document.head.appendChild( s );
};

/**
 * Thin SDK bridge for token fetching. All business logic (stale ID guard,
 * caching, formatting) lives in getShoppingAgentToken.
 *
 * @param {string} [sessionId] - The session ID from Google's SDK.
 * @returns {Promise<object>} Google-formatted token result.
 * @returns {string} returns.sessionId - The session ID.
 * @returns {string} returns.token - The auth token.
 * @returns {number} returns.token_expiry - Token expiration timestamp.
 */
export const handleTokenFunc = async( sessionId ) => {
  const cache = await getShoppingAgentToken( { sessionId } );
  const result = formatTokenForGoogle( cache );
  getShoppingAgentLogger().log( 'tokenFunc resolved', { sessionId, result } );
  return result;
};

/**
 * Fetches the user's cart items from localStorage.
 *
 * @returns {Array<object>} An array of product objects.
 * Returns an empty array if the cart is missing or empty.
 */
export const handleGetCart = () => {
  const user = getStorage( { key: STORAGE_KEY.user, secure: false } );
  const items = user?.shoppingAgentCart?.items;

  if( !Array.isArray( items ) ){
    getShoppingAgentLogger().warn( 'handleGetCart: Missing or malformed cart items in storage', { items } );
    return [];
  }

  return items;
};

/**
 * Event handler for the `google-agent-loaded` event. Sets a global flag
 * to indicate that the script has loaded.
 * @returns {void}
 */
export const handleGoogleAgentLoaded = () => {
  global.shoppingAgentLoaded = true;
};


/**
 * Event handler for the `google-agent-query-sent` event.
 * @returns {void}
 */
export const handleGoogleAgentQuerySubmitted = () => {

  const querySubmissionAction = global.ULTA_SDK?.shoppingAgentModel?.querySubmissionAction;
  const dataCaptureData = querySubmissionAction?.dataCaptureData;
  datacapture.processDataCaptureEvent( dataCaptureData );

};

/**
 * Event handler for the `google-agent-single-item-clicked` event.
 * @returns {void}
 */
export const handleGoogleAgentSingleItemClicked = ( e ) => {

  const skuId = e?.detail?.product?.metadata?.skuId;
  const productId = e?.detail?.product?.id;
  const productCardClickAction = global.ULTA_SDK?.shoppingAgentModel?.productCardClickAction;

  if( !skuId || !productId || !productCardClickAction ){
    return;
  }

  const patchedData = patchDataLayer( {
    dataCaptureData: productCardClickAction?.dataCaptureData,
    values: { shoppingAgentSkuId: skuId, shoppingAgentProductId: productId }
  } );

  try {
    setStorage( {
      key: PENDING_SHOPPING_AGENT_EVENT,
      value: patchedData,
      secure: false
    } );
  }
  catch ( err ){
    getShoppingAgentLogger().error( 'Failed to set pendingShoppingAgentClickEvent in sessionStorage', err );
  }

};

/**
 * Event handler for the `google-agent-add-to-cart` event.
 * Logs event information for monitoring cart updates.
 *
 * @param {object} e - The event object.
 * @returns {void}
 */
export const handleGoogleAgentAddToCart = ( e ) => {
  getShoppingAgentLogger().log( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentAddToCart, e.detail );
};

/**
 * Enriches the addToBagAction with Shopping Agent context and product identifiers.
 *
 * @param {object} params - The parameters.
 * @param {Array<object>} params.products - The list of products from the SDK.
 * @param {object} params.addToBagAction - The original action object.
 * @returns {object} The enriched action object.
 */
const enrichAddToBagAction = ( { products, openVariantPickerAction } ) => {
  const { productId, metadata } = products?.[0] || {};
  const { skuId } = metadata || {};

  return {
    ...openVariantPickerAction,
    variables: {
      moduleParams: {
        isShoppingAgent: true,
        ...( productId && { productId } ),
        ...( skuId && { skuId } )
      }
    }
  };
};

/**
 * Composes a cart update handler (add, update, remove items) initiated by the Google Shopping Agent.
 *
 * @param {object} data - Configuration data (preserved for signature compatibility).
 * @param {object} methods - Functional dependencies.
 * @param {Function} methods.openOverlay - Function to open the product detail overlay.
 * @returns {Function} An async callback that handles cart updates.
 */
export const composeHandleCartUpdate = ( data, methods ) => async( products, action, method, type ) => {
  const { openOverlay } = methods || {};
  const openVariantPickerAction = global.ULTA_SDK?.shoppingAgentModel?.openVariantPickerAction;
  if( type === SHOW_DETAILS ){
    if( !openOverlay || !openVariantPickerAction?.graphql ){
      return;
    }
    shoppingAgentUtil.closeShoppingAgentChat();
    const enrichedAction = enrichAddToBagAction( { products, openVariantPickerAction } );
    openOverlay( {
      type: enrichedAction.navigationType,
      action: enrichedAction,
      crossButtonVisibility:  true
    } );
    return new Promise( ( resolve, reject ) => {
      const eventHandler = ( e ) => {
        window.removeEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentAddToCart, eventHandler );
        resolve( e?.detail );
      };
      window.addEventListener( GOOGLE_SHOPPING_AGENT_EVENT.GoogleAgentAddToCart, eventHandler );
    } );
  }

  const { handlecartaction } = handleEmptyObjects( global.ULTA_SDK?.shoppingAgentModel );

  if( !handlecartaction ){
    return {
      data: null,
      success: false,
      error:
        '[ULTA_SDK] handlecartaction is missing from shoppingAgent on the SDK'
    };
  }

  try {
    const res = await handlecartaction.post( { products, action, method } );
    if( !res ){
      return {
        data: null,
        success: false,
        error:
          '[ULTA_SDK] handlecartaction returned empty response from shoppingAgent on the SDK'
      };
    }

    return res;
  }
  catch ( error ){
    return {
      data: null,
      success: false,
      error: '[ULTA_SDK] handlecartaction - An unexpected error occurred during cart update.'
    };
  }
};


/**
 * Event handler for the `google-agent-open-changed` event.
 * Processes data capture events when the agent's open state changes,
 * specifically looking for toggle click actions.
 *
 * @param {object} e - The event object.
 * @param {object} e.detail - The event details.
 * @param {boolean} e.detail.isOpen - Whether the agent is currently open.
 * @returns {Promise<void>}
 */
export const handleGoogleAgentOpen = async( e ) => {
  if( !e?.detail?.isOpen ){
    return;
  }
  const toggleClickAction = global.ULTA_SDK?.shoppingAgentModel?.toggleClickAction;
  if( !toggleClickAction ){
    return;
  }

  const { dataCaptureData } = handleEmptyObjects( toggleClickAction );

  if( dataCaptureData && !isEmptyObject( dataCaptureData ) ){
    datacapture.processEvents( { dataCapture: { ...dataCaptureData } }, constants.DATACAPTURE_EVENT_TYPE.click );
  }
};
ShoppingAgent.displayName = 'ShoppingAgent';
